// ─────────────────────────────────────────────────────────────────────────────
// KLOTHIQ — SaleCartList Component (FEAT-SALE-ITEMS-001)
// ─────────────────────────────────────────────────────────────────────────────
// Reactive Point-of-Sale multi-item cart table.
// Provides inline price overrides, quantity steppers, discount checks,
// live line totals, and duplicate item pulse animations.
// ─────────────────────────────────────────────────────────────────────────────

import { Input } from '@/components/ui/FormControls';
import { ShoppingBag, Minus, Plus, Trash2, AlertTriangle } from 'lucide-react';
import type { CatalogVariant } from './VariantPickerCombobox';

export interface CartItemData {
  variant_id: string;
  quantity: number;
  unit_price: number;
  discount: number;
}

export interface SaleCartListProps {
  items: CartItemData[];
  variantsMap: Map<string, CatalogVariant>;
  onUpdateItem: (index: number, updated: Partial<CartItemData>) => void;
  onRemoveItem: (index: number) => void;
  highlightedVariantId?: string | null;
  disabled?: boolean;
  lockQuantities?: boolean;
}

export default function SaleCartList({
  items,
  variantsMap,
  onUpdateItem,
  onRemoveItem,
  highlightedVariantId,
  disabled = false,
  lockQuantities = false,
}: SaleCartListProps) {
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border/80 bg-muted/10 p-8 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary mb-3">
          <ShoppingBag className="h-6 w-6" />
        </div>
        <h3 className="text-sm font-semibold text-foreground">No items added yet</h3>
        <p className="mt-1 max-w-sm text-xs text-muted-foreground">
          Use the search bar above to search for products, sizes, and colors to quickly build this
          sale order.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((item, idx) => {
        const variant = variantsMap.get(item.variant_id);
        const availableStock = variant?.available ?? 999;
        const defaultPrice = variant?.defaultPrice ?? 0;
        const isPriceEdited = Math.abs(item.unit_price - defaultPrice) > 0.001;
        const lineSubtotal = Number(item.unit_price) * Number(item.quantity);
        const lineTotal = Math.max(0, lineSubtotal - Number(item.discount || 0));
        const hasDiscountError = Number(item.discount || 0) > lineSubtotal;
        const isAtStockCeiling = item.quantity >= availableStock;
        const isHighlighted = highlightedVariantId === item.variant_id;

        return (
          <div
            key={`${item.variant_id}-${idx}`}
            className={`obsidian-surface relative rounded-xl border bg-card p-4 shadow-sm transition-all ${
              isHighlighted
                ? 'border-primary ring-2 ring-primary ring-offset-2 ring-offset-background'
                : 'border-border hover:border-border/80'
            }`}
          >
            {/* Top row: Title + Size/Color chip + Delete */}
            <div className="flex items-start justify-between gap-3 pb-3 border-b border-border/50">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="text-sm font-semibold text-foreground break-words">
                    {variant?.productName || 'Unknown Product'}
                  </h4>
                  {variant?.size && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-secondary text-secondary-foreground text-xs font-bold shrink-0">
                      {variant.size}
                    </span>
                  )}
                  {variant?.color && (
                    <span className="text-xs text-muted-foreground font-medium shrink-0">
                      · {variant.color}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[11px] font-mono text-muted-foreground break-words">
                    SKU: {variant?.sku || '—'}
                  </span>
                  <span className="text-[11px] text-emerald-600 dark:text-emerald-400 font-medium">
                    ({availableStock} available)
                  </span>
                </div>
              </div>

              {!disabled && !lockQuantities && (
                <button
                  type="button"
                  onClick={() => onRemoveItem(idx)}
                  aria-label="Remove item from sale"
                  className="rounded-lg p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors shrink-0"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Bottom row: Unit Price, Quantity Stepper, Discount, Line Total */}
            <div className="grid grid-cols-2 gap-3 pt-3 sm:grid-cols-4 items-center">
              {/* Unit Price */}
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-muted-foreground">Price (₹)</label>
                  {isPriceEdited && (
                    <span className="text-[10px] font-semibold text-amber-600 dark:text-amber-400">
                      edited
                    </span>
                  )}
                </div>
                <div className="relative mt-1">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                    ₹
                  </span>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    disabled={disabled}
                    aria-label={`Unit price for ${variant?.productName ?? 'item'}`}
                    value={item.unit_price}
                    onChange={(e) =>
                      onUpdateItem(idx, {
                        unit_price: Math.max(0, parseFloat(e.target.value) || 0),
                      })
                    }
                    className="flex h-11 w-full rounded-md border border-input bg-background pl-6 pr-2 text-xs ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </div>
              </div>

              {/* Quantity Stepper */}
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-muted-foreground">Qty</label>
                  {isAtStockCeiling && (
                    <span className="text-[10px] text-amber-600 dark:text-amber-400 font-medium">
                      Max stock
                    </span>
                  )}
                </div>
                <div className="mt-1 flex items-center rounded-md border border-input bg-background">
                  <button
                    type="button"
                    disabled={disabled || lockQuantities || item.quantity <= 1}
                    onClick={() => onUpdateItem(idx, { quantity: Math.max(1, item.quantity - 1) })}
                    className="flex h-11 w-11 items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-l-md transition-colors disabled:opacity-30 disabled:pointer-events-none"
                    aria-label="Decrease quantity"
                  >
                    <Minus className="h-3 w-3" />
                  </button>
                  <Input
                    type="number"
                    min={1}
                    max={availableStock}
                    disabled={disabled || lockQuantities}
                    aria-label={`Quantity for ${variant?.productName ?? 'item'}`}
                    value={item.quantity}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (!isNaN(val)) {
                        const clamped = Math.min(Math.max(1, val), availableStock);
                        onUpdateItem(idx, { quantity: clamped });
                      }
                    }}
                    className="h-11 w-full bg-transparent text-center text-xs font-semibold focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <button
                    type="button"
                    disabled={disabled || lockQuantities || isAtStockCeiling}
                    title={
                      isAtStockCeiling ? `Only ${availableStock} available in stock` : undefined
                    }
                    onClick={() =>
                      onUpdateItem(idx, { quantity: Math.min(availableStock, item.quantity + 1) })
                    }
                    className="flex h-11 w-11 items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-r-md transition-colors disabled:opacity-30 disabled:pointer-events-none"
                    aria-label="Increase quantity"
                  >
                    <Plus className="h-3 w-3" />
                  </button>
                </div>
              </div>

              {/* Discount Input */}
              <div>
                <label className="text-xs font-medium text-muted-foreground">Discount (₹)</label>
                <div className="relative mt-1">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                    ₹
                  </span>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    disabled={disabled}
                    aria-label={`Discount for ${variant?.productName ?? 'item'}`}
                    value={item.discount}
                    onChange={(e) =>
                      onUpdateItem(idx, {
                        discount: Math.max(0, parseFloat(e.target.value) || 0),
                      })
                    }
                    className={`flex h-11 w-full rounded-md border bg-background pl-6 pr-2 text-xs ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                      hasDiscountError
                        ? 'border-destructive focus-visible:ring-destructive'
                        : 'border-input focus-visible:ring-ring'
                    }`}
                    helpLabel="Discount (₹)"
                  />
                </div>
                {hasDiscountError && (
                  <p className="mt-1 text-[10px] text-destructive flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3 shrink-0" /> Max ₹{lineSubtotal.toFixed(2)}
                  </p>
                )}
              </div>

              {/* Line Total */}
              <div className="text-right sm:pl-2">
                <span className="text-xs font-medium text-muted-foreground block">Line Total</span>
                <span className="mt-1 block text-sm font-bold text-foreground">
                  ₹
                  {lineTotal.toLocaleString('en-IN', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
